import React, { useMemo, useRef, useState } from "react";
import styled from "styled-components";
import { colors, shadows } from "../styles/GlobalStyles";
import { rv, mediaQueries } from "../styles/responsive";
import {
  Section,
  SectionDescription,
  SectionHeader,
  SectionLabel,
  SectionTitle,
} from "./ui";
import { ChevronLeftIcon, ChevronRightIcon } from "./ui/Icons";
import useFetchJson from "../hooks/useFetchJson";
import useLockPageScroll from "../hooks/useLockPageScroll";

type PortfolioSkill = {
  language: string;
  detail: string;
};

interface PortfolioItem {
  category: string;
  company: string;
  title: string;
  simpleDescription: string;
  detailDescription: string;
  image?: string;
  skills: PortfolioSkill[];
}

const PortfolioSection = styled(Section)`
  background-color: ${colors.slate[50]};
  padding: ${rv(48, 44)} 0 ${rv(38, 34)};
  display: flex;
  flex-direction: column;
  justify-content: center;

  @media (max-width: 767px) {
    justify-content: flex-start;
    padding: 40px 0 34px;
  }
`;

const PortfolioHeader = styled(SectionHeader)`
  width: min(1120px, calc(100vw - ${rv(48, 32)}));
  margin: 0 auto ${rv(14, 12)};
  text-align: left;

  ${SectionTitle} {
    max-width: 620px;
    margin-bottom: ${rv(8, 8)};
  }

  ${SectionDescription} {
    max-width: 680px;
    margin: 0;
  }

  @media (min-width: 1200px) and (max-height: 820px) {
    ${SectionDescription} {
      display: none;
    }
  }

  @media (max-width: 767px) {
    width: calc(100vw - 40px);
    margin-bottom: 8px;

    ${SectionTitle} {
      margin-bottom: 8px;
    }
  }
`;

const SliderWrapper = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  overflow: hidden;
`;

const SliderHeader = styled.div`
  width: 100%;
  display: none;
  justify-content: flex-end;
  gap: 10px;
  padding: 0 ${rv(56, 20)} ${rv(4, 0)};

  ${mediaQueries.tabletUp} {
    display: flex;
  }
`;

const SliderNavButton = styled.button`
  width: 42px;
  height: 42px;
  display: inline-grid;
  place-items: center;
  border: 1px solid ${colors.slate[200]};
  border-radius: 999px;
  color: ${colors.slate[700]};
  background: ${colors.white};
  box-shadow: 0 8px 20px rgba(15, 23, 42, 0.08);
  cursor: pointer;
  transition: border-color 0.2s ease, color 0.2s ease, transform 0.2s ease;

  &:hover {
    color: ${colors.brand[900]};
    border-color: ${colors.brand[100]};
    transform: translateY(-1px);
  }

  &:focus-visible {
    outline: 3px solid ${colors.brand[100]};
    outline-offset: 2px;
  }
`;

const SliderContainer = styled.div`
  display: flex;
  overflow-x: auto;
  scroll-snap-type: x proximity;
  gap: ${rv(24, 16)};
  padding: ${rv(12, 14)} ${rv(56, 20)} ${rv(18, 22)};
  scroll-behavior: smooth;
  overscroll-behavior-x: contain;
  touch-action: pan-x;

  &::-webkit-scrollbar {
    display: none;
  }
  -ms-overflow-style: none;
  scrollbar-width: none;

  @media (max-width: 767px) {
    gap: 14px;
    padding: 12px 20px 18px;
  }
`;

const PortfolioCard = styled.button`
  width: min(84vw, 330px);
  min-height: 0;
  flex-shrink: 0;
  scroll-snap-align: start;
  border: 1px solid ${colors.slate[100]};
  border-radius: ${rv(24, 18)};
  background: ${colors.white};
  box-shadow: ${shadows.bento};
  overflow: hidden;
  display: flex;
  flex-direction: column;
  text-align: left;
  transition: transform 0.24s ease, box-shadow 0.24s ease,
    border-color 0.24s ease;

  &:hover {
    transform: translateY(-4px);
    border-color: ${colors.slate[200]};
    box-shadow: ${shadows.bentoHover};
  }

  &:focus-visible {
    outline: 3px solid ${colors.brand[100]};
    outline-offset: 3px;
  }

  ${mediaQueries.desktopUp} {
    width: min(24vw, 350px);
  }

  @media (min-width: 1200px) and (max-height: 820px) {
    width: min(23vw, 320px);
  }

  @media (max-width: 767px) {
    width: min(76vw, 292px);
    min-height: 0;
    border-radius: 18px;
  }
`;

const CardVisual = styled.div<{ $bgGradient: string }>`
  position: relative;
  width: 100%;
  aspect-ratio: 1 / 1;
  background: ${({ $bgGradient }) => $bgGradient};
  overflow: hidden;
`;

const CardImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
`;

const CardVisualFallback = styled.div`
  position: absolute;
  inset: 0;
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
  padding: ${rv(24, 18)};
  background: radial-gradient(
      circle at top right,
      rgba(255, 255, 255, 0.46),
      transparent 34%
    ),
    linear-gradient(180deg, rgba(255, 255, 255, 0.02), rgba(15, 23, 42, 0.12));

  @media (max-width: 767px) {
    padding: 16px;
  }
`;

const VisualCategory = styled.span`
  display: inline-flex;
  align-items: center;
  width: fit-content;
  min-height: 28px;
  padding: 6px 11px;
  border-radius: 999px;
  color: ${colors.white};
  background: rgba(15, 23, 42, 0.52);
  backdrop-filter: blur(8px);
  font-size: 11px;
  font-weight: 900;
  letter-spacing: 0.08em;
  text-transform: uppercase;

  @media (max-width: 767px) {
    min-height: 24px;
    padding: 5px 9px;
    font-size: 10px;
  }
`;

const VisualTitle = styled.strong`
  display: block;
  margin-top: 14px;
  color: ${colors.white};
  font-size: ${rv(26, 21)};
  line-height: 1.08;
  letter-spacing: -0.04em;

  @media (max-width: 767px) {
    margin-top: 10px;
    font-size: 19px;
  }
`;

const CardBody = styled.div`
  flex: 1;
  padding: ${rv(18, 18)};
  display: flex;
  flex-direction: column;
  gap: 10px;

  @media (max-width: 767px) {
    padding: 16px;
    gap: 9px;
  }
`;

const CardCompany = styled.span`
  color: ${colors.brand[900]};
  font-size: 12px;
  font-weight: 900;
  letter-spacing: 0.08em;
  text-transform: uppercase;

  @media (max-width: 767px) {
    font-size: 11px;
  }
`;

const CardTitle = styled.h3`
  color: ${colors.slate[900]};
  font-size: ${rv(21, 18)};
  font-weight: 800;
  line-height: 1.25;
  letter-spacing: -0.03em;

  @media (max-width: 767px) {
    font-size: 17px;
  }
`;

const CardDescription = styled.p`
  color: ${colors.slate[500]};
  font-size: ${rv(14, 13)};
  line-height: 1.65;
  display: -webkit-box;
  overflow: hidden;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;

  @media (min-width: 1200px) and (max-height: 820px) {
    display: none;
  }

  @media (max-width: 767px) {
    font-size: 12px;
    line-height: 1.55;
  }
`;

const SkillList = styled.ul`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: auto;
  padding-top: 4px;
  list-style: none;

  @media (min-width: 1200px) and (max-height: 820px) {
    display: none;
  }

  @media (max-width: 767px) {
    display: none;
  }
`;

const SkillItem = styled.li`
  min-width: 0;
  padding: 9px 11px;
  border-radius: 999px;
  background: ${colors.slate[50]};
  border: 1px solid ${colors.slate[100]};
`;

const SkillLanguage = styled.strong`
  color: ${colors.slate[900]};
  font-size: 12px;
  font-weight: 800;
`;

const SkillDetail = styled.span`
  display: none;
`;

const CardFooter = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding-top: 4px;
  margin-top: auto;
`;

const DetailPrompt = styled.span`
  color: ${colors.brand[900]};
  font-size: 13px;
  font-weight: 800;

  @media (max-width: 767px) {
    font-size: 12px;
  }
`;

const MobileHint = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  gap: ${rv(8, 8)};
  margin-top: ${rv(10, 10)};
  opacity: 0.5;

  ${mediaQueries.tabletUp} {
    display: none;
  }

  span {
    font-size: ${rv(12, 11)};
    font-weight: 500;
  }
`;

const ModalOverlay = styled.div`
  position: fixed;
  inset: 0;
  z-index: 1200;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
  background: rgba(15, 23, 42, 0.58);
  backdrop-filter: blur(10px);
  overscroll-behavior: contain;

  @media (max-width: 767px) {
    align-items: flex-end;
    padding: 0;
  }
`;

const ModalPanel = styled.article`
  width: min(980px, 100%);
  max-height: min(86vh, 900px);
  overflow: hidden;
  border-radius: 32px;
  background: ${colors.white};
  box-shadow: 0 30px 80px rgba(15, 23, 42, 0.24);
  display: flex;

  @media (max-width: 767px) {
    width: 100%;
    max-height: 92vh;
    border-radius: 28px 28px 0 0;
  }
`;

const ModalGrid = styled.div`
  width: 100%;
  max-height: inherit;
  min-height: 0;
  overflow: hidden;
  display: grid;
  grid-template-columns: minmax(320px, 340px) minmax(0, 1fr);

  @media (max-width: 1023px) {
    grid-template-columns: 1fr;
    grid-template-rows: auto minmax(0, 1fr);
  }
`;

const ModalVisual = styled.div<{ $bgGradient: string }>`
  position: relative;
  width: 100%;
  aspect-ratio: 1 / 1;
  align-self: start;
  background: ${({ $bgGradient }) => $bgGradient};
  overflow: hidden;
`;

const ModalBody = styled.div`
  padding: ${rv(40, 24)};
  display: flex;
  flex-direction: column;
  gap: 18px;
  min-height: 0;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
  overscroll-behavior: contain;

  @media (max-width: 767px) {
    padding: 22px 20px 28px;
  }
`;

const ModalTopBar = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 20px;
`;

const ModalCompany = styled.span`
  display: inline-flex;
  align-items: center;
  height: 30px;
  padding: 0 12px;
  border-radius: 999px;
  color: ${colors.brand[900]};
  background: ${colors.brand[50]};
  font-size: 11px;
  font-weight: 900;
  letter-spacing: 0.08em;
  text-transform: uppercase;
`;

const ModalCloseButton = styled.button`
  width: 40px;
  height: 40px;
  flex-shrink: 0;
  border: 1px solid ${colors.slate[200]};
  border-radius: 999px;
  background: ${colors.white};
  color: ${colors.slate[700]};
  font-size: 20px;
  line-height: 1;

  &:hover {
    color: ${colors.slate[900]};
    border-color: ${colors.slate[300]};
  }
`;

const ModalTitle = styled.h3`
  color: ${colors.slate[900]};
  font-size: ${rv(34, 28)};
  font-weight: 900;
  line-height: 1.12;
  letter-spacing: -0.045em;
`;

const ModalCategory = styled.span`
  color: ${colors.slate[500]};
  font-size: 13px;
  font-weight: 800;
  letter-spacing: 0.08em;
  text-transform: uppercase;
`;

const ModalDescription = styled.div`
  display: flex;
  flex-direction: column;
  gap: 14px;
  color: ${colors.slate[600]};
  font-size: ${rv(16, 14)};
  line-height: 1.8;

  p {
    white-space: pre-line;
  }
`;

const ModalSectionTitle = styled.h4`
  color: ${colors.slate[900]};
  font-size: 16px;
  font-weight: 800;
  letter-spacing: -0.02em;
`;

const ModalSkillList = styled.ul`
  display: flex;
  flex-direction: column;
  gap: 12px;
  list-style: none;
`;

const ModalSkillItem = styled.li`
  padding: 16px 18px;
  border: 1px solid ${colors.slate[100]};
  border-radius: 18px;
  background: ${colors.slate[50]};
`;

const ModalSkillHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 8px;
`;

const ModalSkillBadge = styled.span`
  display: inline-flex;
  align-items: center;
  height: 28px;
  padding: 0 10px;
  border-radius: 999px;
  color: ${colors.brand[900]};
  background: ${colors.brand[50]};
  font-size: 12px;
  font-weight: 900;
`;

const ModalSkillText = styled.p`
  color: ${colors.slate[600]};
  font-size: 14px;
  line-height: 1.75;
`;

const gradients = [
  `linear-gradient(135deg, ${colors.brand[50]}, #dbeafe)`,
  `linear-gradient(135deg, #dcfce7, #d1fae5)`,
  `linear-gradient(135deg, #f3e8ff, #fce7f3)`,
  `linear-gradient(135deg, #fef3c7, #fed7aa)`,
  `linear-gradient(135deg, #e0e7ff, #c7d2fe)`,
  `linear-gradient(135deg, #cffafe, #a5f3fc)`,
];

const Portfolio: React.FC = () => {
  const {
    data: portfolioData,
    loading: portfolioLoading,
    error: portfolioError,
  } = useFetchJson<PortfolioItem[]>("portfolio.json");
  const sliderRef = useRef<HTMLDivElement>(null);
  const [selectedIndex, setSelectedIndex] = useState<number | null>(null);

  const selectedItem = useMemo(() => {
    if (!portfolioData || selectedIndex === null) return null;
    return portfolioData[selectedIndex] ?? null;
  }, [portfolioData, selectedIndex]);

  useLockPageScroll(Boolean(selectedItem));

  if (portfolioLoading) return null;
  if (portfolioError) return null;
  if (!portfolioData) return null;

  const scrollPortfolio = (direction: "previous" | "next") => {
    if (!sliderRef.current) return;

    const scrollAmount = sliderRef.current.clientWidth * 0.82;
    sliderRef.current.scrollBy({
      left: direction === "next" ? scrollAmount : -scrollAmount,
      behavior: "smooth",
    });
  };

  return (
    <PortfolioSection id="portfolio">
      <PortfolioHeader>
        <SectionLabel>Selected Work</SectionLabel>
        <SectionTitle>Portfolio Highlights</SectionTitle>
        <SectionDescription>
          주요 프로젝트를 가볍게 훑어보고, 카드 선택 시 구현 배경과 기술
          스택을 자세히 확인할 수 있습니다.
        </SectionDescription>
      </PortfolioHeader>

      <SliderWrapper>
        <SliderHeader aria-label="포트폴리오 카드 탐색">
          <SliderNavButton
            type="button"
            aria-label="이전 포트폴리오 보기"
            onClick={() => scrollPortfolio("previous")}
          >
            <ChevronLeftIcon />
          </SliderNavButton>
          <SliderNavButton
            type="button"
            aria-label="다음 포트폴리오 보기"
            onClick={() => scrollPortfolio("next")}
          >
            <ChevronRightIcon />
          </SliderNavButton>
        </SliderHeader>
        <SliderContainer ref={sliderRef}>
          {portfolioData.map((item, index) => {
            const gradient = gradients[index % gradients.length];
            return (
              <PortfolioCard
                key={`${item.company}-${item.title}`}
                type="button"
                onClick={() => setSelectedIndex(index)}
              >
                <CardVisual $bgGradient={gradient}>
                  {item.image ? (
                    <CardImage
                      src={`${process.env.REACT_APP_IMAGE_PATH}${item.image}`}
                      alt={`${item.title} 대표 이미지`}
                    />
                  ) : (
                    <CardVisualFallback>
                      <VisualCategory>{item.category}</VisualCategory>
                      <VisualTitle>{item.title}</VisualTitle>
                    </CardVisualFallback>
                  )}
                </CardVisual>
                <CardBody>
                  <CardCompany>{item.company}</CardCompany>
                  <CardTitle>{item.title}</CardTitle>
                  <CardDescription>{item.simpleDescription}</CardDescription>

                  <SkillList>
                    {item.skills.slice(0, 2).map((skill) => (
                      <SkillItem key={`${item.title}-${skill.language}`}>
                        <SkillLanguage>{skill.language}</SkillLanguage>
                        <SkillDetail>{skill.detail}</SkillDetail>
                      </SkillItem>
                    ))}
                  </SkillList>

                  <CardFooter>
                    <DetailPrompt>상세 프로젝트 보기</DetailPrompt>
                    <ChevronRightIcon />
                  </CardFooter>
                </CardBody>
              </PortfolioCard>
            );
          })}
        </SliderContainer>
      </SliderWrapper>

      <MobileHint>
        <ChevronLeftIcon />
        <span>스와이프하여 더 보기</span>
        <ChevronRightIcon />
      </MobileHint>

      {selectedItem && (
        <ModalOverlay
          role="dialog"
          aria-modal="true"
          aria-label={`${selectedItem.title} 상세 정보`}
          onClick={() => setSelectedIndex(null)}
          onWheel={(event) => event.stopPropagation()}
          onTouchMove={(event) => event.stopPropagation()}
        >
          <ModalPanel
            data-lenis-prevent
            onClick={(event) => event.stopPropagation()}
          >
            <ModalGrid>
              <ModalVisual
                $bgGradient={gradients[(selectedIndex ?? 0) % gradients.length]}
              >
                {selectedItem.image ? (
                  <CardImage
                    src={`${process.env.REACT_APP_IMAGE_PATH}${selectedItem.image}`}
                    alt={`${selectedItem.title} 대표 이미지`}
                  />
                ) : (
                  <CardVisualFallback>
                    <VisualCategory>{selectedItem.category}</VisualCategory>
                    <VisualTitle>{selectedItem.title}</VisualTitle>
                  </CardVisualFallback>
                )}
              </ModalVisual>

              <ModalBody>
                <ModalTopBar>
                  <div>
                    <ModalCompany>{selectedItem.company}</ModalCompany>
                    <ModalTitle>{selectedItem.title}</ModalTitle>
                    <ModalCategory>{selectedItem.category}</ModalCategory>
                  </div>
                  <ModalCloseButton
                    type="button"
                    aria-label="상세 팝업 닫기"
                    onClick={() => setSelectedIndex(null)}
                  >
                    ×
                  </ModalCloseButton>
                </ModalTopBar>

                <ModalDescription>
                  <p>{selectedItem.simpleDescription}</p>
                  <p>{selectedItem.detailDescription}</p>
                </ModalDescription>

                <div>
                  <ModalSectionTitle>Skill Stack</ModalSectionTitle>
                  <ModalSkillList>
                    {selectedItem.skills.map((skill) => (
                      <ModalSkillItem
                        key={`${selectedItem.title}-${skill.language}`}
                      >
                        <ModalSkillHeader>
                          <ModalSkillBadge>{skill.language}</ModalSkillBadge>
                        </ModalSkillHeader>
                        <ModalSkillText>{skill.detail}</ModalSkillText>
                      </ModalSkillItem>
                    ))}
                  </ModalSkillList>
                </div>
              </ModalBody>
            </ModalGrid>
          </ModalPanel>
        </ModalOverlay>
      )}
    </PortfolioSection>
  );
};

export default Portfolio;
